import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiErrorDetail } from "../lib/api";
import { Button } from "../components/ui/button";
import { ShieldAlert } from "lucide-react";
import { toast, Toaster } from "sonner";

const SEV = { critical: "badge-high", high: "badge-high", medium: "badge-medium", low: "badge-low" };

export default function FraudQueue() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/fraud-queue").then((r) => setItems(r.data.items || []));
  }, []);

  useEffect(() => { load(); }, [load]);

  async function resolve(id) {
    try {
      await api.patch(`/admin/fraud-flags/${id}/resolve`);
      toast.success("Flag resolved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <div className="p-8 space-y-6" data-testid="fraud-page">
      <Toaster position="top-right" />
      <header className="flex items-center gap-3">
        <ShieldAlert className="w-6 h-6 text-destructive" />
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">{t("admin_pages.fraud_section")}</div>
          <h1 className="font-heading text-3xl font-bold mt-1">{t("admin_pages.fraud_title")}</h1>
        </div>
      </header>

      <div className="bg-white border border-border rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="fraud-table">
          <thead className="text-xs uppercase tracking-wider text-stone-500">
            <tr>
              <th className="text-left font-medium py-3 px-5">Flag Type</th>
              <th className="text-left font-medium py-3 px-5">Severity</th>
              <th className="text-left font-medium py-3 px-5">Merchant / Claim</th>
              <th className="text-left font-medium py-3 px-5">Notes</th>
              <th className="text-left font-medium py-3 px-5">Created</th>
              <th className="text-right font-medium py-3 px-5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.id} className="border-t border-border table-row-hover" data-testid={`fraud-row-${f.id}`}>
                <td className="py-3 px-5 capitalize">{f.flag_type.replace(/_/g, " ")}</td>
                <td className="py-3 px-5">
                  <span className={`badge-status ${SEV[f.severity] || "badge-medium"}`}>{f.severity}</span>
                </td>
                <td className="py-3 px-5 font-mono text-xs">
                  <div>M: {f.merchant_id?.slice(0, 8) || "—"}</div>
                  {f.claim_id && <div>C: {f.claim_id.slice(0, 8)}…</div>}
                </td>
                <td className="py-3 px-5 max-w-md">{f.notes}</td>
                <td className="py-3 px-5 text-xs text-muted-foreground">
                  {new Date(f.created_at).toLocaleString()}
                </td>
                <td className="py-3 px-5 text-right">
                  <Button size="sm" variant="outline" data-testid={`resolve-${f.id}`} onClick={() => resolve(f.id)}>
                    {t("admin_pages.fraud_resolve")}
                  </Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="py-10 text-center text-muted-foreground">No open fraud flags</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
